import React, { useCallback, useEffect, useState } from 'react';
import { ArrowsPointingOutIcon, ArrowsPointingInIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import proDocumentsService from '../../../../services/proDocuments.service';
import ModalHeader from '../../../../components/ui/ModalHeader';
import { useModalDrag } from '../../../../hooks/useModalDrag';
import { saveAs, saveBlob } from '../../../../utils/saveFile';
import PrintButton from '../../../../components/printing/PrintButton';

/**
 * Draggable document preview lightbox (PDF embed / image / download fallback).
 *
 * Extracted from DocsTab so the multi-PRO Documents view on Search Results can
 * reuse it: it only needs the pronumber + map_recno to fetch, so it takes the
 * minimal shape rather than a full ProDocumentRow.
 */
export interface DocPreviewLightboxProps {
  /** Numeric order pronumber, or a "T"-prefixed template pronumber ("T110"). */
  pronumber: number | string;
  /**
   * cms_map recno of the document. Required unless `loadBlob` is supplied —
   * documents from the invoice / leg-document sources have no cms_map row.
   */
  mapRecno?: number;
  /**
   * Which per-PRO table `mapRecno` belongs to. cmsinvoice_map recnos share the
   * integer space with cms_map recnos, so omitting this on an invoice document
   * silently previews a DIFFERENT file. Ignored when `loadBlob` is supplied.
   */
  source?: 'cms' | 'invoice';
  /**
   * Overrides how the blob is fetched. The Search Results Documents view
   * passes its own loader because its rows can come from three different
   * document tables, only one of which the per-PRO route understands. MUST be
   * referentially stable (useCallback/useMemo) — it is a fetch dependency.
   */
  loadBlob?: () => Promise<Blob>;
  filename: string;
  filetype: string | null | undefined;
  onClose: () => void;
}

const IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tif', 'tiff', 'webp'];

export default function DocPreviewLightbox({
  pronumber,
  mapRecno,
  source,
  loadBlob,
  filename,
  filetype,
  onClose,
}: DocPreviewLightboxProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [namedUrl, setNamedUrl] = useState<string | null>(null);
  const [namedUrlFailed, setNamedUrlFailed] = useState(false);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(!expanded);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const ft = (filetype || '').toLowerCase();
  const isImage = IMAGE_TYPES.includes(ft);
  const isPdf = ft === 'pdf';

  // A PDF the per-PRO route can address gets a NAMED url instead of a blob.
  //
  // The embedded viewer is the one surface we cannot style or label: handed a
  // `URL.createObjectURL` blob it has no name and no headers to read, so it titles
  // itself with the blob's UUID and its Save As proposes that UUID too. The outer
  // header shows "invoice_RAVAGO1_3150964_30744838.pdf" while the viewer inside shows
  // "0c3bc557-4e0f-4d…", and a user saving from inside the viewer gets the UUID on
  // disk. Pointing the embed at a URL the browser fetches ITSELF fixes both, because
  // the SAS carries Content-Disposition with the real filename.
  //
  // A `loadBlob` row (Search Results) comes from a table this route cannot address, and
  // an image has no inner viewer to mislabel — both keep the blob path.
  // `namedUrlFailed` covers the OTHER failure mode: the API call succeeded but the
  // browser could not load the url it returned (SAS expired mid-session, storage
  // unreachable). Without it the embed just sits broken, because the catch below only
  // sees the API call. Not reset on a prop change — the lightbox mounts fresh per
  // preview, and the worst case is one more document on the blob path.
  const canUseNamedUrl = isPdf && mapRecno !== undefined && !loadBlob && !namedUrlFailed;

  // Fetch the blob via authenticated request and turn it into an object URL.
  // Direct <img src=/api/...> would skip the Bearer token and 401.
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setObjectUrl(null);
    setNamedUrl(null);
    setBlob(null);
    setError(null);
    // `loadBlob` wins when given; otherwise fall back to the per-PRO route.
    // One of the two is always present (see the props doc), but guard anyway
    // so a bad call renders an error instead of requesting /documents/undefined.
    const fetchBlob = loadBlob
      ?? (mapRecno === undefined
        ? null
        : () => proDocumentsService.previewBlob(pronumber, mapRecno, source));

    const loadViaBlob = () => {
      if (!fetchBlob) {
        setError('Preview is unavailable for this document.');
        return;
      }
      fetchBlob()
        .then((fetched) => {
          if (cancelled) return;
          url = URL.createObjectURL(fetched);
          setObjectUrl(url);
          setBlob(fetched);
        })
        .catch((e) => {
          if (cancelled) return;
          setError(e?.message || 'Failed to load preview');
        });
    };

    if (canUseNamedUrl && mapRecno !== undefined) {
      // Never fatal: storage not configured, or a SAS the account refuses, degrades to
      // the blob preview (right document, UUID name) rather than no preview at all.
      proDocumentsService.previewUrl(pronumber, mapRecno, source)
        .then((r) => {
          if (cancelled) return;
          setNamedUrl(r.url);
        })
        .catch(() => {
          if (!cancelled) loadViaBlob();
        });
    } else {
      loadViaBlob();
    }

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [pronumber, mapRecno, source, loadBlob, canUseNamedUrl]);

  // Send the stored PDF to a CUPS printer. Only for documents the per-PRO route can
  // address — a `loadBlob` row comes from a table the print route does not know, and
  // the server refuses anything but a PDF (409), so the button follows the same rule.
  /** Save the document under the name the header shows.
   *
   *  The embedded viewer has its own download button and we cannot label it — it names
   *  the file from the url it was handed, which is why the named url exists at all. This
   *  one does not depend on any of that: `saveAs` opens the picker pre-filled with
   *  `filename` BEFORE fetching, so the suggested name is ours no matter what the viewer
   *  would have proposed. It is also the only download an IMAGE preview has ever had. */
  const downloadDoc = useCallback(() => {
    const getBlob = async (): Promise<Blob> => {
      if (blob) return blob;            // blob path already fetched it
      if (loadBlob) return loadBlob();  // Search Results' own loader
      if (mapRecno === undefined) throw new Error('Download is unavailable.');
      return proDocumentsService.downloadBlob(pronumber, mapRecno, source);
    };
    void saveAs(filename, getBlob);
  }, [blob, loadBlob, mapRecno, pronumber, source, filename]);

  const printEndpoint = isPdf && mapRecno !== undefined && !loadBlob
    ? `/pros/${pronumber}/documents/${mapRecno}/print${source === 'invoice' ? '?source=invoice' : ''}`
    : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Document preview"
      className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/60"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className={`relative flex flex-col overflow-hidden rounded-lg bg-panel ${expanded ? 'w-[calc(100vw-2rem)] h-[calc(100dvh-2rem)]' : 'w-[916px] max-w-[90vw] h-[772px] max-h-[90dvh]'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={filename || 'Document Preview'}
          onClose={onClose}
          closeLabel="Close preview"
          dragHandleProps={expanded ? undefined : dragHandleProps}
          actions={
            <>
              <button
                type="button"
                onClick={downloadDoc}
                aria-label={`Download ${filename || 'document'}`}
                title={`Download ${filename || 'document'}`}
                className="mr-2 rounded-md p-1 text-cmp-modal-header-muted transition-colors hover:text-cmp-modal-header-fg focus:outline-none focus:ring-2 focus:ring-indigo-400"
              >
                <ArrowDownTrayIcon className="size-5" aria-hidden="true" />
              </button>
              {printEndpoint && (
                <PrintButton
                  endpoint={printEndpoint}
                  iconOnly
                  title={`Print ${filename || 'document'}`}
                  className="mr-2 rounded-md p-1 text-cmp-modal-header-muted transition-colors hover:text-cmp-modal-header-fg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              )}
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              aria-label={expanded ? 'Restore preview size' : 'Expand preview'}
              aria-pressed={expanded}
              title={expanded ? 'Restore preview size' : 'Expand preview'}
              className="mr-2 rounded-md p-1 text-cmp-modal-header-muted transition-colors hover:text-cmp-modal-header-fg focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              {expanded ? <ArrowsPointingInIcon className="size-5" aria-hidden="true" /> : <ArrowsPointingOutIcon className="size-5" aria-hidden="true" />}
            </button>
            </>
          }
        />
        <div className="min-h-0 flex-1 overflow-auto p-2">
          {error ? (
            <div className="px-8 py-12 text-center text-red-600 dark:text-red-300">{error}</div>
          ) : !objectUrl && !namedUrl ? (
            <div className="px-8 py-12 text-center text-muted" role="status">
              Loading preview…
            </div>
          ) : isPdf ? (
            <embed
              src={(namedUrl ?? objectUrl) as string}
              title={filename || 'Document Preview'}
              type="application/pdf"
              width="100%"
              height="100%"
              className="block"
              onError={() => { if (namedUrl) setNamedUrlFailed(true); }}
            />
          ) : isImage ? (
            // `?? undefined` only to satisfy the narrowing: an image never takes the
            // named-URL path, so reaching here means objectUrl is set.
            <img
              src={objectUrl ?? undefined}
              alt={filename}
              className="h-full w-full object-contain"
            />
          ) : (
            <button
              type="button"
              onClick={() => { if (blob) void saveBlob(blob, filename); }}
              className="block w-full px-6 py-10 text-center text-blue-600 dark:text-blue-400 underline"
            >
              Download {filename}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
